/**
 * PluginDetailView — full-width detail page for a selected plugin,
 * shown in the main content area when a plugin is selected from the sidebar.
 */

import type { PluginDefinition } from "@magic-md/core";
import type { PluginInfo } from "@magic-md/engine-client/protocol";
import type {
    ActionDeclaration,
    ApiDeclaration,
    JSONSchema,
    PluginExample,
    PluginManifest,
    PluginSourceInfo,
    PluginUI,
    SmartIntakeAcceptRule,
} from "@magic-md/plugin-sdk";
import { CollapsibleSidebar } from "@magic-md/plugin-sdk";
import { useT } from "@magic-md/i18n/react";
import {
    ArrowLeft,
    BookOpen,
    ChevronRight,
    Code2,
    Eye,
    FileText,
    Filter,
    Layers,
    Monitor,
    PanelLeftClose,
    PanelLeftOpen,
    Puzzle,
    Server,
    Settings2,
    Sparkles,
    Type,
    Zap,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { usePluginUI } from "../hooks/usePluginUI";
import { cn } from "../lib/utils";
import {
    getPluginSourceDetailRows,
} from "../plugins/plugin-source-metadata";
import { useEditorStore } from "../store/editor-store";
import { usePluginExecutionStore } from "../store/plugin-execution-store";
import { useSettingsStore } from "../store/settings-store";
import { getThemeAccentStyle } from "../theme/runtime/theme-accents";
import { PluginSourceBadge } from "./PluginSourceBadge";

/* ── Main Component ── */

export function PluginDetailView({ pluginName }: { pluginName: string }) {
    const t = useT("editor");
    const setSelectedPluginName = useEditorStore(
        (s) => s.setSelectedPluginName,
    );
    const parsedDocument = useEditorStore((s) => s.parsedDocument);
    const enginePlugin = usePluginExecutionStore((s) =>
        s.enginePlugins.find((candidate) => candidate.name === pluginName),
    );

    const plugin = usePluginUI(pluginName);

    // Find instances of this plugin in the current document
    const instances = useMemo(() => {
        if (!parsedDocument?.definitions) return [];
        return Array.from(parsedDocument.definitions.values()).filter(
            (def) => def.pluginName === pluginName,
        );
    }, [parsedDocument, pluginName]);

    const configFields = useMemo(() => {
        if (!plugin) return [];
        return deriveConfigParamsFromSchema(plugin.manifest.configSchema);
    }, [plugin]);

    if (!plugin) {
        return (
            <div className="flex flex-col items-center justify-center h-full gap-3">
                <Puzzle size={32} className="text-muted-foreground/30" />
                <p className="text-sm text-muted-foreground">
                    {t("pluginDetail.notFound", { name: pluginName })}
                </p>
                <button
                    type="button"
                    className="text-xs text-primary hover:underline"
                    onClick={() => setSelectedPluginName(null)}
                >
                    {t("pluginDetail.action.goBack")}
                </button>
            </div>
        );
    }

    const m = plugin.manifest;
    const source = mergePluginDetailSource(
        plugin.source,
        enginePlugin?.source as PluginSourceInfo | undefined,
    );
    const sourceVersion = source?.version ?? enginePlugin?.version ?? m.version;
    const runtimeApis = mergeApiDeclarations(m.apis, enginePlugin?.apis);
    const smartIntakeCards = buildSmartIntakeCards({
        plugin,
        enginePlugin,
        source,
    });

    return (
        <div className="flex flex-col h-full bg-background">
            {/* Top bar */}
            <div className="flex items-center gap-2 h-10 px-4 border-b border-border shrink-0 bg-muted/20">
                <button
                    type="button"
                    className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                    onClick={() => setSelectedPluginName(null)}
                >
                    <ArrowLeft size={14} />
                    <span>{t("common.back")}</span>
                </button>
                <div className="w-px h-4 bg-border" />
                <span className="text-xs text-muted-foreground">
                    {t("pluginDetail.detailTitle")}
                </span>
            </div>

            {/* Content */}
            <div className="flex-1 min-h-0 overflow-auto">
                <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
                    {/* Header */}
                    <HeaderSection
                        manifest={m}
                        source={source}
                        onOpenSettings={
                            m.settingsSchema || m.defaults
                                ? () => {
                                    setSelectedPluginName(null);
                                    useSettingsStore.getState().setActiveSection("plugin");
                                    useSettingsStore.getState().setSearchQuery(pluginName);
                                    useEditorStore.getState().setSidebarView("settings");
                                }
                                : undefined
                        }
                    />

                    <Section
                        title={t("pluginDetail.sourceInfo")}
                        icon={<Server size={14} />}
                        boxed
                    >
                        <PluginSourceDetails
                            source={source}
                            fallbackVersion={sourceVersion}
                        />
                    </Section>

                    {/* Overview */}
                    {m.overview && (
                        <Section
                            title={t("pluginDetail.overview")}
                            icon={<BookOpen size={14} />}
                            boxed
                        >
                            <p className="text-sm text-muted-foreground leading-relaxed">
                                {m.overview}
                            </p>
                        </Section>
                    )}

                    {/* Actions */}
                    {m.actions.length > 0 && (
                        <Section
                            title={t("pluginDetail.section.actions")}
                            icon={<Zap size={14} />}
                            count={m.actions.length}
                        >
                            <ActionList actions={m.actions} />
                        </Section>
                    )}

                    {/* Runtime APIs */}
                    {runtimeApis.length > 0 && (
                        <Section
                            title={t("pluginDetail.runtimeApis")}
                            icon={<Code2 size={14} />}
                            count={runtimeApis.length}
                        >
                            <ApiWorkspace
                                apis={runtimeApis}
                                pluginName={m.name}
                            />
                        </Section>
                    )}

                    {/* Smart Intake */}
                    {smartIntakeCards.length > 0 && (
                        <Section
                            title={t("pluginDetail.smartIntake")}
                            icon={<Sparkles size={14} />}
                            count={smartIntakeCards.length}
                        >
                            <SmartIntakeCards cards={smartIntakeCards} />
                        </Section>
                    )}

                    {/* Config Params */}
                    {configFields.length > 0 && (
                        <Section
                            title={t("pluginDetail.configParameters")}
                            icon={<Settings2 size={14} />}
                            count={configFields.length}
                        >
                            <ConfigParamList params={configFields} />
                        </Section>
                    )}

                    {/* Examples */}
                    {m.examples && m.examples.length > 0 && (
                        <Section
                            title={t("pluginDetail.usageExamples")}
                            icon={<FileText size={14} />}
                            count={m.examples.length}
                        >
                            <ExampleList examples={m.examples} />
                        </Section>
                    )}

                    {/* Current Document Instances */}
                    {instances.length > 0 && (
                        <Section
                            title={t("pluginDetail.documentInstances")}
                            icon={<Layers size={14} />}
                            count={instances.length}
                        >
                            <InstanceList instances={instances} />
                        </Section>
                    )}

                    {/* Render Modes */}
                    <Section
                        title={t("pluginDetail.renderModes")}
                        icon={<Eye size={14} />}
                    >
                        <RenderModes render={m.render} />
                    </Section>

                    {/* Output Schema */}
                    {m.outputSchema &&
                        typeof m.outputSchema !== "function" && (
                            <Section
                                title={t("pluginDetail.outputSchema")}
                                icon={<Code2 size={14} />}
                            >
                                <SchemaTree schema={m.outputSchema} />
                            </Section>
                        )}

                    {/* Backend / Sidecar Binding */}
                    {(m.backends?.length ?? 0) > 0 && (
                        <Section
                            title={t("pluginDetail.backendBinding")}
                            icon={<Server size={14} />}
                        >
                            <BackendBinding
                                manifest={m}
                            />
                        </Section>
                    )}
                </div>
            </div>
        </div>
    );
}

type SmartIntakeProviderDisplay = "ui" | "runtime";

interface SmartIntakeProviderCardDisplay {
    provider: SmartIntakeProviderDisplay;
    accepts: SmartIntakeAcceptRule[];
    dynamicAccepts: boolean;
}

function buildSmartIntakeCards({
    plugin,
    enginePlugin,
    source,
}: {
    plugin: PluginUI;
    enginePlugin?: PluginInfo;
    source?: PluginSourceInfo;
}): SmartIntakeProviderCardDisplay[] {
    const cards: SmartIntakeProviderCardDisplay[] = [];

    if (plugin.smartIntake) {
        cards.push({
            provider: "ui",
            accepts: dedupeSmartIntakeAcceptRules(plugin.smartIntake.accepts ?? []),
            dynamicAccepts: !plugin.smartIntake.accepts?.length,
        });
    }

    const runtimeProvider = enginePlugin?.smartIntake?.runtime;
    const hasRuntimeEndpoint =
        !!source?.smartIntakeProposeUrl || !!source?.smartIntakeBuildPlanUrl;

    if (runtimeProvider || hasRuntimeEndpoint) {
        cards.push({
            provider: "runtime",
            accepts: dedupeSmartIntakeAcceptRules(runtimeProvider?.accepts ?? []),
            dynamicAccepts: !runtimeProvider?.accepts?.length,
        });
    }

    return cards;
}

function dedupeSmartIntakeAcceptRules(
    accepts: SmartIntakeAcceptRule[],
): SmartIntakeAcceptRule[] {
    const seen = new Set<string>();
    const deduped: SmartIntakeAcceptRule[] = [];
    for (const rule of accepts) {
        const key = [
            rule.kind,
            rule.mime ?? "",
            ...(rule.extensions ?? []).map((extension) => extension.toLowerCase()),
        ].join(":");
        if (seen.has(key)) continue;
        seen.add(key);
        deduped.push(rule);
    }
    return deduped;
}

function mergePluginDetailSource(
    uiSource: PluginSourceInfo | undefined,
    engineSource: PluginSourceInfo | undefined,
): PluginSourceInfo | undefined {
    if (!uiSource) return engineSource;
    if (!engineSource) return uiSource;
    if (isDefaultLocalPluginSource(uiSource)) return engineSource;
    return {
        ...engineSource,
        ...uiSource,
        smartIntakeProposeUrl:
            uiSource.smartIntakeProposeUrl ?? engineSource.smartIntakeProposeUrl,
        smartIntakeBuildPlanUrl:
            uiSource.smartIntakeBuildPlanUrl ?? engineSource.smartIntakeBuildPlanUrl,
    };
}

function isDefaultLocalPluginSource(source: PluginSourceInfo): boolean {
    return source.kind === "local" &&
        Object.entries(source).every(([key, value]) => key === "kind" || value == null);
}

/* ── Sub-components ── */

function Section({
    title,
    icon,
    count,
    boxed = false,
    children,
}: {
    title: string;
    icon?: React.ReactNode;
    count?: number;
    boxed?: boolean;
    children: React.ReactNode;
}) {
    return (
        <div>
            <SectionHeader icon={icon} title={title} badge={count} />
            {boxed ? (
                <div className="rounded-lg border border-border bg-card p-4">
                    {children}
                </div>
            ) : (
                children
            )}
        </div>
    );
}

function SectionHeader({
    icon,
    title,
    badge,
}: {
    icon?: React.ReactNode;
    title: string;
    badge?: number;
}) {
    return (
        <div className="flex items-center gap-2 mb-3">
            {icon ? <span className="text-muted-foreground">{icon}</span> : null}
            <h2 className="text-sm font-semibold text-foreground">{title}</h2>
            {badge != null ? (
                <span className="text-[10px] font-medium text-muted-foreground bg-muted/40 rounded px-1.5 py-0.5 tabular-nums">
                    {badge}
                </span>
            ) : null}
        </div>
    );
}

function Badge({
    children,
    mono,
    color,
}: {
    children: React.ReactNode;
    mono?: boolean;
    color?: "default" | "primary" | "success" | "warning";
}) {
    return (
        <span
            className={cn(
                "inline-block rounded-md border px-1.5 py-0.5 text-[10px]",
                mono && "font-mono",
                color === "primary"
                    ? "border-primary/30 bg-primary/5 text-primary"
                    : color === "success"
                        ? "theme-accent-chip"
                        : color === "warning"
                            ? "theme-accent-chip"
                            : "border-border/50 bg-muted/30 text-muted-foreground",
            )}
            style={
                color === "success"
                    ? getThemeAccentStyle("success")
                    : color === "warning"
                        ? getThemeAccentStyle("warning")
                        : undefined
            }
        >
            {children}
        </span>
    );
}

function SmartIntakeCards({
    cards,
}: {
    cards: SmartIntakeProviderCardDisplay[];
}) {
    const t = useT("editor");
    return (
        <div className="grid gap-2 sm:grid-cols-2">
            {cards.map((card) => (
                <div
                    key={card.provider}
                    className="rounded-lg border border-border/30 bg-card px-3 py-2"
                >
                    <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-foreground/80">
                        <Sparkles size={12} className="text-muted-foreground" />
                        {t(`pluginDetail.smartIntake.provider.${card.provider}`)}
                    </div>
                    <div className="flex items-start gap-2">
                        <span className="shrink-0 pt-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground/60">
                            {t("pluginDetail.smartIntake.accepts")}
                        </span>
                        <div className="flex min-w-0 flex-wrap gap-1.5">
                            {card.accepts.map((rule, index) => (
                                <Badge
                                    key={`${card.provider}:${rule.kind}:${rule.mime ?? ""}:${rule.extensions?.join(",") ?? ""}:${index}`}
                                >
                                    {formatSmartIntakeAcceptRule(t, rule)}
                                </Badge>
                            ))}
                            {card.dynamicAccepts ? (
                                <Badge>{t("pluginDetail.smartIntake.dynamicAccepts")}</Badge>
                            ) : null}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

function formatSmartIntakeAcceptRule(
    t: ReturnType<typeof useT>,
    rule: SmartIntakeAcceptRule,
): string {
    const parts = [formatSmartIntakeKind(t, rule.kind)];
    if (rule.mime) parts.push(rule.mime);
    if (rule.extensions && rule.extensions.length > 0) {
        parts.push(
            rule.extensions
                .map((extension) => extension.startsWith(".") ? extension : `.${extension}`)
                .join(", "),
        );
    }
    return parts.join(" · ");
}

function formatSmartIntakeKind(
    t: ReturnType<typeof useT>,
    kind: SmartIntakeAcceptRule["kind"],
): string {
    switch (kind) {
        case "text":
        case "html":
        case "url":
        case "json":
        case "har":
        case "file":
        case "folder":
        case "table":
        case "magic-fragment":
            return t(`pluginDetail.smartIntake.kind.${kind}`);
        default:
            return kind;
    }
}

function HeaderSection({
    manifest,
    source,
    onOpenSettings,
}: {
    manifest: PluginManifest;
    source?: PluginSourceInfo;
    onOpenSettings?: () => void;
}) {
    const t = useT("editor");
    return (
        <div className="flex items-start gap-4">
            {/* Icon */}
            <div className="w-14 h-14 rounded-xl border border-border bg-muted/30 flex items-center justify-center text-2xl shrink-0">
                {manifest.icon ?? "🧩"}
            </div>

            {/* Title & meta */}
            <div className="min-w-0 flex-1">
                <div className="flex items-start gap-2">
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                        <h1 className="truncate text-xl font-bold text-foreground tracking-tight">
                            {manifest.title ?? manifest.name}
                        </h1>
                        <Badge color="success">{t("pluginDetail.installed")}</Badge>
                    </div>
                    <PluginSourceBadge
                        source={source}
                        compact
                        className="mt-1 ml-auto"
                    />
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                    {manifest.description}
                </p>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                    {manifest.version && (
                        <Badge>v{manifest.version}</Badge>
                    )}
                    {manifest.author && <Badge>{manifest.author}</Badge>}
                    {manifest.backends?.map((b) => (
                        <Badge key={b.id} color="primary" mono>
                            {b.id}
                        </Badge>
                    ))}
                    {manifest.homepage && (
                        <a
                            href={manifest.homepage}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[10px] text-primary hover:underline"
                        >
                            {t("pluginDetail.homepage")} ↗
                        </a>
                    )}
                    {onOpenSettings && (
                        <button
                            type="button"
                            onClick={onOpenSettings}
                            className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[10px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                        >
                            <Settings2 size={12} />
                            {t("pluginDetail.action.openSettings")}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

function PluginSourceDetails({
    source,
    fallbackVersion,
}: {
    source?: PluginSourceInfo;
    fallbackVersion?: string;
}) {
    const t = useT("editor");
    const rows = getPluginSourceDetailRows(source, fallbackVersion);
    return (
        <div className="space-y-2">
            {rows.map((row) => {
                const value = row.key === "type" ? t(row.value) : row.value;
                return (
                    <div
                        key={`${row.key}:${row.value}`}
                        className="grid gap-1 text-sm sm:grid-cols-[132px_minmax(0,1fr)]"
                    >
                        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                            {t(row.labelKey)}
                        </div>
                        {row.href ? (
                            <a
                                href={row.href}
                                target="_blank"
                                rel="noreferrer"
                                className="min-w-0 truncate text-xs text-primary hover:underline"
                                title={value}
                            >
                                {value}
                            </a>
                        ) : (
                            <div
                                className={cn(
                                    "min-w-0 truncate text-xs text-foreground/80",
                                    row.mono && "font-mono",
                                )}
                                title={value}
                            >
                                {value}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function ActionList({ actions }: { actions: ActionDeclaration[] }) {
    const t = useT("editor");
    return (
        <div className="space-y-1">
            {actions.map((action) => (
                <div
                    key={action.name}
                    className="flex items-center gap-2 rounded-lg border border-border/30 px-3 py-2"
                >
                    {action.icon && (
                        <span className="text-base shrink-0">
                            {action.icon}
                        </span>
                    )}
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                            <code className="text-xs font-mono font-semibold text-foreground/80">
                                {action.name}
                            </code>
                            {action.label && action.label !== action.name && (
                                <span className="text-xs text-muted-foreground">
                                    — {action.label}
                                </span>
                            )}
                        </div>
                        {action.description && (
                            <p className="text-[10px] text-muted-foreground/60 mt-0.5">
                                {action.description}
                            </p>
                        )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                        {action.primary && (
                            <Badge color="primary">{t("pluginDetail.primary")}</Badge>
                        )}
                        {action.dangerous && (
                            <Badge color="warning">{t("pluginDetail.dangerous")}</Badge>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
}

type DisplayApiDeclaration = Omit<ApiDeclaration, "paramsSchema" | "resultSchema"> & {
    paramsSchema?: JSONSchema;
    resultSchema?: JSONSchema;
};

type DisplayApiInput = Omit<DisplayApiDeclaration, "paramsSchema" | "resultSchema"> & {
    paramsSchema?: JSONSchema | Record<string, unknown>;
    resultSchema?: JSONSchema | Record<string, unknown>;
};

function mergeApiDeclarations(
    manifestApis: ApiDeclaration[] | undefined,
    engineApis: DisplayApiInput[] | undefined,
): DisplayApiDeclaration[] {
    const manifestByName = new Map(
        (manifestApis ?? []).map((api) => [api.name, api] as const),
    );
    const engineByName = new Map(
        (engineApis ?? []).map((api) => [api.name, api] as const),
    );
    const orderedNames = [
        ...(manifestApis ?? []).map((api) => api.name),
        ...(engineApis ?? [])
            .map((api) => api.name)
            .filter((name) => !manifestByName.has(name)),
    ];

    const mergedApis: DisplayApiDeclaration[] = [];
    for (const name of orderedNames) {
        const manifestApi = manifestByName.get(name);
        const engineApi = engineByName.get(name);
        if (!manifestApi && !engineApi) continue;
        mergedApis.push({
            ...manifestApi,
            ...engineApi,
            name,
            paramsSchema: preferDeclaredSchema(
                engineApi?.paramsSchema,
                manifestApi?.paramsSchema,
            ),
            resultSchema: preferDeclaredSchema(
                engineApi?.resultSchema,
                manifestApi?.resultSchema,
            ),
        });
    }
    return mergedApis;
}

function preferDeclaredSchema(
    engineSchema: JSONSchema | Record<string, unknown> | undefined,
    manifestSchema: JSONSchema | Record<string, unknown> | undefined,
): JSONSchema | undefined {
    if (hasDeclaredProperties(engineSchema)) return engineSchema as JSONSchema;
    if (hasDeclaredProperties(manifestSchema)) return manifestSchema as JSONSchema;
    return (engineSchema ?? manifestSchema) as JSONSchema | undefined;
}

function hasDeclaredProperties(
    schema: JSONSchema | Record<string, unknown> | undefined,
): boolean {
    if (!schema || typeof schema !== "object") return false;
    const properties = (schema as { properties?: unknown }).properties;
    return !!properties && typeof properties === "object" && Object.keys(properties).length > 0;
}

function ApiWorkspace({
    apis,
    pluginName,
}: {
    apis: DisplayApiDeclaration[];
    pluginName: string;
}) {
    const t = useT("editor");
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [query, setQuery] = useState("");
    const [selectedApiName, setSelectedApiName] = useState<string | null>(
        apis[0]?.name ?? null,
    );

    useEffect(() => {
        if (selectedApiName && apis.some((api) => api.name === selectedApiName)) {
            return;
        }
        setSelectedApiName(apis[0]?.name ?? null);
    }, [apis, selectedApiName]);

    const filteredApis = useMemo(() => {
        const lowered = query.trim().toLowerCase();
        if (!lowered) return apis;
        return apis.filter((api) =>
            [api.name, api.label, api.description, api.externalEffect]
                .filter((value): value is string => Boolean(value))
                .some((value) => value.toLowerCase().includes(lowered)),
        );
    }, [apis, query]);

    const selectedApi = apis.find((api) => api.name === selectedApiName) ?? null;

    return (
        <div
            data-testid="plugin-api-workspace"
            className="rounded-lg border border-border bg-card overflow-hidden"
        >
            <CollapsibleSidebar
                collapsed={sidebarCollapsed}
                storageKey={`plugin-detail/${pluginName}/api-sidebar`}
                defaultWidth={280}
                minWidth={220}
                maxWidth={380}
                minMainWidth={420}
                collapsedWidth={42}
                className="h-[360px] min-h-[300px] min-w-0 overflow-hidden"
                sidebarClassName="border-r border-border/40 bg-card"
                mainClassName="h-full min-h-0 min-w-0 overflow-hidden bg-card"
                collapsedSidebar={
                    <button
                        type="button"
                        className="flex h-full w-full items-start justify-center pt-3 text-muted-foreground hover:bg-muted/30 hover:text-foreground"
                        onClick={() => setSidebarCollapsed(false)}
                        title={t("common.expand")}
                    >
                        <PanelLeftOpen size={14} />
                    </button>
                }
                sidebar={
                    <aside className="flex h-full min-h-0 flex-col">
                        <div className="border-b border-border/40 p-3 space-y-2">
                            <div className="flex items-center gap-2">
                                <Code2 size={12} className="text-muted-foreground shrink-0" />
                                <span className="min-w-0 flex-1 truncate text-xs font-medium text-foreground/80">
                                    {t("pluginDetail.runtimeApis")}
                                </span>
                                <button
                                    type="button"
                                    className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                                    onClick={() => setSidebarCollapsed(true)}
                                    title={t("common.collapse")}
                                >
                                    <PanelLeftClose size={12} />
                                </button>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <Filter size={11} className="text-muted-foreground/50 shrink-0" />
                                <input
                                    type="text"
                                    placeholder={t("pluginDetail.api.filterPlaceholder")}
                                    value={query}
                                    onChange={(event) => setQuery(event.target.value)}
                                    className="min-w-0 flex-1 bg-transparent text-xs text-foreground placeholder:text-muted-foreground/40 outline-none"
                                />
                            </div>
                        </div>

                        <div className="min-h-0 flex-1 overflow-auto divide-y divide-border/20">
                            {filteredApis.length === 0 ? (
                                <div className="px-3 py-6 text-center">
                                    <p className="text-xs text-muted-foreground/50">
                                        {t("pluginDetail.api.empty")}
                                    </p>
                                </div>
                            ) : (
                                filteredApis.map((api) => (
                                    <ApiIndexRow
                                        key={api.name}
                                        api={api}
                                        selected={api.name === selectedApiName}
                                        onSelect={() => setSelectedApiName(api.name)}
                                    />
                                ))
                            )}
                        </div>
                    </aside>
                }
            >
                {selectedApi ? (
                    <ApiDetailPanel api={selectedApi} />
                ) : (
                    <div className="flex h-full min-h-0 items-center justify-center p-6 text-center">
                        <p className="text-xs text-muted-foreground/50">
                            {t("pluginDetail.api.noSelection")}
                        </p>
                    </div>
                )}
            </CollapsibleSidebar>
        </div>
    );
}

function ApiIndexRow({
    api,
    selected,
    onSelect,
}: {
    api: DisplayApiDeclaration;
    selected: boolean;
    onSelect: () => void;
}) {
    return (
        <button
            type="button"
            className={cn(
                "relative flex w-full items-start gap-2 px-3 py-2 text-left transition-colors",
                selected
                    ? "font-semibold text-primary"
                    : "text-muted-foreground hover:text-foreground",
            )}
            onClick={onSelect}
        >
            <span
                className={cn(
                    "absolute left-0 top-1/2 h-4 w-0.5 -translate-y-1/2 rounded-full transition-opacity",
                    selected ? "bg-primary opacity-100" : "opacity-0",
                )}
            />
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                    <span className="min-w-0 truncate text-xs font-mono font-medium">
                        {api.name}
                    </span>
                    {api.dangerous ? (
                        <span className="shrink-0 text-[9px] font-semibold uppercase text-muted-foreground/50">
                            !
                        </span>
                    ) : null}
                </div>
                {api.label && api.label !== api.name ? (
                    <div className="mt-0.5 truncate text-[10px] text-muted-foreground/55">
                        {api.label}
                    </div>
                ) : null}
            </div>
            <span className="shrink-0 text-[10px] font-mono text-muted-foreground/45">
                {api.externalEffect ?? "none"}
            </span>
        </button>
    );
}

function ApiDetailPanel({ api }: { api: DisplayApiDeclaration }) {
    const t = useT("editor");
    const paramsRows = schemaPropertyRows(api.paramsSchema);
    const resultRows = schemaPropertyRows(api.resultSchema);

    return (
        <div className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden bg-card">
            <div className="border-b border-border/40 p-3 space-y-3">
                <div className="flex items-start gap-2 min-w-0">
                    <Code2 size={14} className="mt-0.5 shrink-0 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-mono font-semibold text-foreground">
                            {api.name}
                        </span>
                        {api.label && api.label !== api.name ? (
                            <span className="mt-0.5 block text-xs text-muted-foreground/70">
                                {api.label}
                            </span>
                        ) : null}
                    </div>
                </div>

                <div className="flex flex-wrap gap-1.5">
                    <Badge mono>
                        {t("pluginDetail.documentEffect")}:{" "}
                        {formatEffectLabel(t, api.documentEffect ?? "none")}
                    </Badge>
                    <Badge
                        mono
                        color={
                            api.externalEffect === "write"
                                ? "warning"
                                : api.externalEffect === "read"
                                    ? "success"
                                    : undefined
                        }
                    >
                        {t("pluginDetail.externalEffect")}:{" "}
                        {formatEffectLabel(t, api.externalEffect ?? "none")}
                    </Badge>
                    {typeof api.timeoutSecs === "number" ? (
                        <Badge mono>
                            {t("pluginDetail.timeoutSecs", {
                                seconds: api.timeoutSecs,
                            })}
                        </Badge>
                    ) : null}
                    {api.dangerous ? (
                        <Badge color="warning">{t("pluginDetail.dangerous")}</Badge>
                    ) : null}
                </div>
            </div>

            <div
                data-testid="plugin-api-detail-scroll"
                className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-3"
            >
                <div className="space-y-4">
                    {api.description ? (
                        <div>
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/50">
                                {t("common.description")}
                            </span>
                            <p className="mt-1 text-xs leading-relaxed text-muted-foreground/70">
                                {api.description}
                            </p>
                        </div>
                    ) : null}

                    <div
                        data-testid="plugin-api-schema-stack"
                        className="flex flex-col gap-3"
                    >
                        <ApiSchemaSection
                            title={t("pluginDetail.api.paramsSchema")}
                            schema={api.paramsSchema}
                            rows={paramsRows}
                        />
                        <ApiSchemaSection
                            title={t("pluginDetail.api.resultSchema")}
                            schema={api.resultSchema}
                            rows={resultRows}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

function ApiSchemaSection({
    title,
    schema,
    rows,
}: {
    title: string;
    schema: JSONSchema | undefined;
    rows: ApiSchemaRow[];
}) {
    const t = useT("editor");
    return (
        <div className="min-w-0 space-y-2">
            <div className="flex items-center gap-2">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/50">
                    {title}
                </span>
                <span className="text-[10px] font-mono text-muted-foreground/35">
                    {rows.length}
                </span>
            </div>
            {rows.length === 0 ? (
                <div className="px-0 py-1 text-[10px] text-muted-foreground/50">
                    {t("pluginDetail.api.noProperties")}
                </div>
            ) : (
                <div className="rounded-md border border-border/30 overflow-hidden">
                    <table className="w-full text-[10px]">
                        <thead>
                            <tr className="border-b border-border/20 text-muted-foreground/50">
                                <th className="px-2 py-1 text-left font-semibold uppercase tracking-wider">
                                    {t("common.name")}
                                </th>
                                <th className="px-2 py-1 text-left font-semibold uppercase tracking-wider">
                                    {t("common.type")}
                                </th>
                                <th className="px-2 py-1 text-left font-semibold uppercase tracking-wider">
                                    {t("common.description")}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border/10">
                            {rows.map((row) => (
                                <tr key={row.name}>
                                    <td className="px-2 py-1 font-mono font-medium text-foreground/70">
                                        {row.name}
                                        {row.required ? null : (
                                            <span className="text-muted-foreground/35">?</span>
                                        )}
                                    </td>
                                    <td className="px-2 py-1 font-mono text-primary/60">
                                        {row.type}
                                    </td>
                                    <td className="px-2 py-1 text-muted-foreground/60">
                                        {row.description}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            <details className="rounded-md border border-border/30">
                <summary className="cursor-pointer px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/50 hover:text-foreground">
                    {t("pluginDetail.api.rawSchema")}
                </summary>
                <div className="border-t border-border/20 px-2 py-1.5">
                    <SchemaTree schema={schema as JSONSchema} />
                </div>
            </details>
        </div>
    );
}

function formatEffectLabel(
    t: ReturnType<typeof useT>,
    effect: string,
): string {
    if (effect === "none" || effect === "read" || effect === "write") {
        return t(`pluginDetail.effect.${effect}`);
    }
    return effect;
}

interface ApiSchemaRow {
    name: string;
    type: string;
    description: string;
    required: boolean;
}

function schemaPropertyRows(schema: JSONSchema | undefined): ApiSchemaRow[] {
    if (!schema || typeof schema !== "object" || !schema.properties) return [];
    const required = new Set(schema.required ?? []);
    return Object.entries(schema.properties)
        .filter((entry): entry is [string, JSONSchema] => !!entry[1])
        .map(([name, property]) => ({
            name,
            type: schemaTypeLabel(property),
            description: property.description ?? "",
            required: required.has(name),
        }));
}

function schemaTypeLabel(schema: JSONSchema | undefined): string {
    if (!schema || typeof schema !== "object") return "unknown";
    const unionSchemas = schema.anyOf ?? schema.oneOf;
    if (Array.isArray(unionSchemas) && unionSchemas.length > 0) {
        return unionSchemas
            .map((item) => schemaTypeLabel(item as JSONSchema))
            .join(" | ");
    }
    if (Array.isArray(schema.enum) && schema.enum.length > 0) {
        return `enum(${schema.enum.map((value) => JSON.stringify(value)).join(" | ")})`;
    }
    if (schema.const !== undefined) return `const(${JSON.stringify(schema.const)})`;
    if (schema.type === "array" && schema.items) {
        return `array<${schemaTypeLabel(schema.items)}>`;
    }
    if (Array.isArray(schema.type)) return schema.type.join(" | ");
    if (schema.type) return schema.type;
    if (schema.properties) return "object";
    return "unknown";
}

function RenderModes({
    render,
}: {
    render: { inline: boolean; block: boolean };
}) {
    const t = useT("editor");
    return (
        <div className="flex items-center gap-3">
            <RenderModeCard
                label={t("pluginDetail.render.inline")}
                icon={<Type size={14} />}
                enabled={render.inline}
            />
            <RenderModeCard
                label={t("pluginDetail.render.block")}
                icon={<Monitor size={14} />}
                enabled={render.block}
            />
        </div>
    );
}

function RenderModeCard({
    label,
    icon,
    enabled,
}: {
    label: string;
    icon: React.ReactNode;
    enabled: boolean;
}) {
    const t = useT("editor");
    return (
        <div
            className={cn(
                "flex items-center gap-2 rounded-lg border px-3 py-2 flex-1",
                enabled
                    ? "theme-accent-subtle"
                    : "border-border/30 bg-muted/10 opacity-50",
            )}
            style={enabled ? getThemeAccentStyle("success") : undefined}
        >
            <span
                className={cn(
                    enabled
                        ? "theme-accent-text"
                        : "text-muted-foreground/30",
                )}
                style={enabled ? getThemeAccentStyle("success") : undefined}
            >
                {icon}
            </span>
            <span className="text-xs font-medium text-foreground">
                {label}
            </span>
            <span
                className={cn(
                    "ml-auto text-[10px] font-semibold uppercase tracking-wider",
                    enabled ? "theme-accent-text" : "text-muted-foreground/40",
                )}
                style={enabled ? getThemeAccentStyle("success") : undefined}
            >
                {enabled ? t("settings.boolean.on") : t("settings.boolean.off")}
            </span>
        </div>
    );
}

function BackendBinding({
    manifest,
}: {
    manifest: PluginManifest;
}) {
    const t = useT("editor");
    const setSelectedSidecarId = useEditorStore((s) => s.setSelectedSidecarId);
    const setSidebarView = useEditorStore((s) => s.setSidebarView);
    const backends = manifest.backends ?? [];

    return (
        <div className="space-y-3">
            {/* Static bindings */}
            {backends.length > 0 && (
                <div className="space-y-2">
                    <div className="flex items-center gap-2">
                        <span className="text-[10px] text-muted-foreground/50 uppercase tracking-wider font-semibold">
                            {t(backends.length > 1 ? "pluginDetail.staticBindings" : "pluginDetail.staticBinding")}
                        </span>
                    </div>
                    {backends.map((backend) => (
                        <div key={backend.id} className="space-y-2">
                            <button
                                type="button"
                                className="flex items-center gap-3 w-full rounded-lg border border-border/30 px-3 py-2.5 text-left hover:bg-muted/30 transition-colors group"
                                onClick={() => {
                                    setSidebarView("engine");
                                    setSelectedSidecarId(backend.id);
                                }}
                            >
                                <Server
                                    size={14}
                                    className="text-primary/60 shrink-0"
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5">
                                        <code className="text-xs font-mono font-semibold text-foreground/80">
                                            {backend.id}
                                        </code>
                                    </div>
                                    <code className="text-[10px] font-mono text-muted-foreground/50">
                                        {backend.command}
                                    </code>
                                </div>
                            </button>

                            {/* Capabilities */}
                            {(backend.capabilities?.length ?? 0) > 0 && (
                                <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="text-[10px] text-muted-foreground/50 mr-1">
                                        {t("common.capabilities")}:
                                    </span>
                                    {backend.capabilities?.map((c) => (
                                        <code
                                            key={c}
                                            className="text-[10px] font-mono text-primary/70 bg-primary/5 border border-primary/10 rounded px-1.5 py-0.5"
                                        >
                                            {c}
                                        </code>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}


        </div>
    );
}

function SchemaTree({ schema }: { schema: JSONSchema }) {
    if (!schema || typeof schema !== "object") {
        return (
            <div className="text-xs text-muted-foreground/50">
                —
            </div>
        );
    }

    return (
        <div className="font-mono text-xs">
            <SchemaNode schema={schema} depth={0} />
        </div>
    );
}

function SchemaNode({
    schema,
    name,
    depth,
}: {
    schema: JSONSchema | undefined;
    name?: string;
    depth: number;
}) {
    const [open, setOpen] = useState(depth < 1);

    if (!schema || typeof schema !== "object") {
        return (
            <div className={cn("py-0.5 text-muted-foreground/40", depth > 0 && "ml-4")}>
                {name && (
                    <span className="font-medium text-foreground/80">
                        {name}{" "}
                    </span>
                )}
                <span>unknown</span>
            </div>
        );
    }

    const hasChildren =
        schema.type === "object" &&
        schema.properties &&
        Object.keys(schema.properties).length > 0;
    const isArray = schema.type === "array";

    return (
        <div className={cn(depth > 0 && "ml-4")}>
            <button
                type="button"
                className={cn(
                    "flex items-center gap-1.5 py-0.5 text-left w-full rounded",
                    hasChildren
                        ? "hover:bg-muted/30 cursor-pointer"
                        : "cursor-default",
                )}
                onClick={() => hasChildren && setOpen(!open)}
            >
                {hasChildren ? (
                    <ChevronRight
                        size={10}
                        className={cn(
                            "text-muted-foreground/40 shrink-0 transition-transform",
                            open && "rotate-90",
                        )}
                    />
                ) : (
                    <span className="w-2.5 shrink-0" />
                )}
                {name && (
                    <span className="text-foreground/80 font-medium">
                        {name}
                    </span>
                )}
                <span className="text-primary/60">{schema.type}</span>
                {isArray && schema.items && (
                    <span className="text-muted-foreground/40">
                        [{schema.items.type ?? "unknown"}]
                    </span>
                )}
                {schema.description && (
                    <span className="text-muted-foreground/40 ml-1 truncate font-sans text-[10px]">
                        — {schema.description}
                    </span>
                )}
            </button>
            {open && hasChildren && schema.properties && (
                <div className="border-l border-border/30 ml-1">
                    {Object.entries(schema.properties).map(([key, sub]) => (
                        <SchemaNode
                            key={key}
                            schema={sub}
                            name={key}
                            depth={depth + 1}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function InstanceList({
    instances,
}: {
    instances: PluginDefinition[];
}) {
    const t = useT("editor");
    return (
        <div className="space-y-1">
            {instances.map((def) => (
                <div
                    key={def.id}
                    className="flex items-center gap-2 rounded-lg border border-border/30 px-3 py-2"
                >
                    <div className="flex-1 min-w-0">
                        <code className="text-xs font-mono font-semibold text-foreground/80">
                            {def.id}
                        </code>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                        {def.output ? (
                            (def.output as Record<string, unknown>)?.__error ? (
                                <Badge color="warning">{t("pluginDetail.error")}</Badge>
                            ) : (
                                <Badge color="success">{t("pluginDetail.hasOutput")}</Badge>
                            )
                        ) : (
                            <Badge>{t("pluginDetail.noOutput")}</Badge>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
}

interface DisplayConfigParam {
    name: string;
    type: string;
    description: string;
    required?: boolean;
    defaultValue?: string;
}

function ConfigParamList({ params }: { params: DisplayConfigParam[] }) {
    const t = useT("editor");
    return (
        <div className="rounded-lg border border-border/40 overflow-hidden">
            <table className="w-full text-left text-xs">
                <thead>
                    <tr className="bg-muted/30 border-b border-border/30">
                        <th className="px-3 py-2 text-[10px] font-semibold text-muted-foreground/60 uppercase tracking-wider">
                            {t("common.name")}
                        </th>
                        <th className="px-3 py-2 text-[10px] font-semibold text-muted-foreground/60 uppercase tracking-wider">
                            {t("common.type")}
                        </th>
                        <th className="px-3 py-2 text-[10px] font-semibold text-muted-foreground/60 uppercase tracking-wider">
                            {t("common.description")}
                        </th>
                        <th className="px-3 py-2 text-[10px] font-semibold text-muted-foreground/60 uppercase tracking-wider">
                            {t("common.default")}
                        </th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-border/20">
                    {params.map((p) => (
                        <tr key={p.name} className="hover:bg-muted/20 transition-colors">
                            <td className="px-3 py-2 whitespace-nowrap">
                                <div className="flex items-center gap-1">
                                    <code className="text-xs font-mono font-semibold text-foreground/80">
                                        {p.name}
                                    </code>
                                    {p.required && (
                                        <span
                                            className="theme-accent-text text-[9px] font-bold"
                                            style={getThemeAccentStyle("error")}
                                        >
                                            *
                                        </span>
                                    )}
                                </div>
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                                <code className="text-[10px] font-mono text-primary/60">
                                    {p.type}
                                </code>
                            </td>
                            <td className="px-3 py-2 text-muted-foreground">
                                {p.description}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">
                                <span className="text-[10px] font-mono text-muted-foreground/50">
                                    {p.defaultValue ?? "—"}
                                </span>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function deriveConfigParamsFromSchema(schema: JSONSchema | undefined): DisplayConfigParam[] {
    if (!schema?.properties) return [];

    const required = new Set(schema.required ?? []);

    return Object.entries(schema.properties)
        .filter((entry): entry is [string, JSONSchema] => !!entry[1])
        .map(([name, property]) => ({
            name,
            type: schemaTypeLabel(property),
            description: property.description ?? "",
            required: required.has(name),
            defaultValue:
                property.default === undefined
                    ? undefined
                    : JSON.stringify(property.default),
        }));
}

function ExampleList({ examples }: { examples: PluginExample[] }) {
    const t = useT("editor");
    const [activeIndex, setActiveIndex] = useState(0);
    const active = examples[activeIndex];

    return (
        <div className="space-y-3">
            {/* Tab bar for multiple examples */}
            {examples.length > 1 && (
                <div className="flex items-center gap-1 border-b border-border/30 pb-2">
                    {examples.map((ex, i) => (
                        <button
                            type="button"
                            key={ex.title}
                            className={cn(
                                "px-2.5 py-1 rounded-md text-xs font-medium transition-colors",
                                i === activeIndex
                                    ? "bg-primary/10 text-primary"
                                    : "text-muted-foreground hover:text-foreground hover:bg-muted/30",
                            )}
                            onClick={() => setActiveIndex(i)}
                        >
                            {ex.title}
                        </button>
                    ))}
                </div>
            )}

            {/* Active example */}
            {active && (
                <div className="space-y-2">
                    {examples.length <= 1 && (
                        <p className="text-xs font-semibold text-foreground">
                            {active.title}
                        </p>
                    )}
                    {active.description && (
                        <p className="text-xs text-muted-foreground">
                            {active.description}
                        </p>
                    )}
                    <div className="rounded-lg border border-border/30 overflow-hidden">
                        <div className="flex items-center px-3 py-1.5 bg-muted/30 border-b border-border/30">
                            <span className="text-[10px] text-muted-foreground/50 uppercase tracking-wider font-semibold">
                                {t("pluginDetail.markdown")}
                            </span>
                            <CopyButton text={active.markdown} />
                        </div>
                        <pre className="px-3 py-2.5 text-[11px] font-mono text-foreground/70 leading-relaxed overflow-x-auto whitespace-pre-wrap">
                            {active.markdown}
                        </pre>
                    </div>
                </div>
            )}
        </div>
    );
}

function CopyButton({ text }: { text: string }) {
    const t = useT("editor");
    const [copied, setCopied] = useState(false);

    return (
        <button
            type="button"
            className="ml-auto text-[10px] text-muted-foreground/50 hover:text-foreground transition-colors"
            onClick={() => {
                navigator.clipboard.writeText(text);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
            }}
        >
            {copied ? t("common.copied") : t("common.copy")}
        </button>
    );
}
